import type { ComponentProps, MouseEvent, ReactNode } from 'react';

import { Spinner } from '@playbook/atoms/spinner';
import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type ButtonVariant =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'ghost'
	| 'destructive';

const DefaultButtonVariant: ButtonVariant = 'tertiary' as const;

export type ButtonSize = Extract<Size, 'sm' | 'md'>;

const DefaultButtonSize: ButtonSize = 'md' as const;

type ButtonType = 'button' | 'reset' | 'submit';

const DefaultButtonType: ButtonType = 'button' as const;

type ButtonEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface ButtonDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

/**
 *	ARIA is admitted the way `data-*` is, on the argument
 *	`atoms/pagination-button` makes: a button that opens something cannot know
 *	what it opened, so whatever arranges the two writes `aria-expanded` and
 *	`aria-haspopup` from outside.
 */
interface ButtonAriaAttributes {
	[key: `aria-${string}`]: string | number | boolean | undefined;
}

interface ButtonProperties
	extends ButtonEventHandlers, ButtonDataAttributes, ButtonAriaAttributes {
	variant?: ButtonVariant;
	size?: ButtonSize;
	type?: ButtonType;
	disabled?: boolean;

	/**
	 *	Shows a spinner in the leading icon's place and stops the button from
	 *	being pressed, while staying focusable and announced as busy. `disabled`
	 *	wins when both are set.
	 */
	loading?: boolean;

	label: string;

	leadingIcon?: ReactNode;
	trailingIcon?: ReactNode;

	/**
	 *	The `id` of a popover this button opens, which the browser then toggles
	 *	and light-dismisses on its own. `modules/calendar` opens its list of
	 *	months this way.
	 */
	popoverTarget?: string;
}

/**
 *	Cancelling the click stops everything a press would set off: the caller's
 *	handler, a form submit and a popover toggle. The button is not `disabled`,
 *	so it keeps its focus and its colours while it loads.
 */
function preventPress(event: MouseEvent<HTMLButtonElement>): void {
	event.preventDefault();
}

export function Button({
	variant = DefaultButtonVariant,
	size = DefaultButtonSize,
	type = DefaultButtonType,
	disabled = false,
	loading = false,

	label,

	leadingIcon,
	trailingIcon,

	popoverTarget,

	onClick,

	...props
}: ButtonProperties): ReactNode {
	const isLoading: boolean = loading && !disabled;

	const className: string = cn(
		css.button,
		css[variant],
		css[size],
		isLoading && css.loading
	);

	return (
		<button
			{...props}
			type={type}
			popoverTarget={popoverTarget}
			data-variant={variant}
			data-size={size}
			className={className}
			disabled={disabled}
			aria-busy={isLoading || undefined}
			aria-disabled={isLoading || undefined}
			onClick={isLoading ? preventPress : onClick}>
			{isLoading ? <Spinner size={size} /> : leadingIcon}
			{label}
			{trailingIcon}
		</button>
	);
}
